import React, { useState } from 'react';
import { Check, Copy, Loader, Trash2, Lock } from 'lucide-react';
import { PiSealCheckDuotone } from 'react-icons/pi';

// Shared building blocks for the agent-connection onboarding wizards
// (Cursor + Claude Code). Both integrations follow the same shape: a
// short guided walkthrough that reveals one step at a time, ending with
// the credential fields — and, once connected, a compact summary card
// with a single Disconnect action.

export const INPUT_CLS =
  'w-full rounded-md border border-[color:var(--border-default)] bg-surface px-3 py-2 text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] outline-none transition-colors focus:border-[color:var(--text-primary)]';

// ─── Copy button ────────────────────────────────────────────────────────────

export function CopyBtn({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(text); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12px] font-medium transition-colors ${
        copied
          ? 'border-[color:var(--feedback-success)] bg-success-soft text-success'
          : 'border-[color:var(--border-default)] bg-surface text-[color:var(--text-primary)] hover:bg-surface-hover'
      }`}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? 'Copied!' : label}
    </button>
  );
}

// ─── Code / prompt box ──────────────────────────────────────────────────────

export function CodeBox({ children, label, copyText }) {
  return (
    <div className="mt-2 overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)]">
      {label && (
        <div className="flex items-center justify-between border-b border-[color:var(--border-default)] px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">{label}</span>
          <CopyBtn text={copyText || children} />
        </div>
      )}
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-[13px] leading-relaxed text-[color:var(--text-primary)]">
        {children}
      </pre>
    </div>
  );
}

// ─── Page hero ──────────────────────────────────────────────────────────────

export function IntegrationHero({ iconSrc, title, connected }) {
  return (
    <div className="mb-6 flex items-start gap-4">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[color:var(--border-default)] bg-surface p-2">
        <img
          src={iconSrc}
          alt={title}
          className="h-7 w-7 object-contain"
          onError={e => { e.target.style.display = 'none'; }}
        />
      </div>
      <div className="flex items-center gap-2.5 pt-1">
        <h1 className="text-[16px] font-semibold text-[color:var(--text-primary)]">{title}</h1>
        {connected && (
          <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-medium text-success">
            <PiSealCheckDuotone size={11} /> Connected
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Connected summary card ─────────────────────────────────────────────────
//
// Shown instead of the walkthrough once the integration is live. Status is
// already conveyed by the "Connected" badge in the hero, so this is just a
// short confirmation line and a Disconnect button that removes the record.
// Reconnecting means re-running the walkthrough — there's no in-place edit.
export function ConnectedCard({ onDisconnect, disconnecting }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[color:var(--feedback-success)] bg-success-soft px-4 py-3">
      <p className="flex items-center gap-1.5 text-[13px] font-medium text-success">
        <PiSealCheckDuotone size={14} /> Connected and ready for Heal.
      </p>
      <button
        type="button"
        onClick={onDisconnect}
        disabled={disconnecting}
        className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--feedback-error)] bg-surface px-2.5 py-1.5 text-[12px] font-medium text-danger transition-colors hover:bg-danger-soft disabled:opacity-50"
      >
        <Trash2 size={12} /> {disconnecting ? 'Disconnecting…' : 'Disconnect'}
      </button>
    </div>
  );
}

// ─── Wizard ─────────────────────────────────────────────────────────────────
//
// A controlled vertical stepper. `steps` is an array of
//   { title, body, final?, continueLabel? }
// Only the active step is expanded; completed steps collapse to a clickable
// summary, and upcoming steps are locked. The final step renders its own
// footer (the credential form + Save); every other step gets a Continue button.
export function Wizard({ steps, current, setCurrent }) {
  return (
    <ol className="overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-5 py-2">
      {steps.map((step, i) => {
        const status = i < current ? 'done' : i === current ? 'active' : 'locked';
        const isLast = i === steps.length - 1;
        return (
          <li key={i} className="flex gap-4">
            {/* Rail: numbered node + connector */}
            <div className="flex flex-col items-center">
              <button
                type="button"
                onClick={() => { if (status === 'done') setCurrent(i); }}
                disabled={status !== 'done'}
                aria-label={status === 'done' ? `Edit step ${i + 1}` : undefined}
                className={`mt-4 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-colors ${
                  status === 'done'
                    ? 'cursor-pointer bg-success-soft text-success ring-1 ring-[color:var(--feedback-success)] hover:brightness-95'
                    : status === 'active'
                      ? 'bg-brand text-ink-onBrand'
                      : 'bg-surface-hover text-[color:var(--text-muted)] ring-1 ring-[color:var(--border-default)]'
                }`}
              >
                {status === 'done' ? <Check size={13} /> : status === 'locked' ? <Lock size={11} /> : i + 1}
              </button>
              {!isLast && (
                <div className={`my-1 w-px flex-1 ${status === 'done' ? 'bg-[color:var(--feedback-success)]' : 'bg-[color:var(--border-subtle)]'}`} />
              )}
            </div>

            {/* Content */}
            <div className={`min-w-0 flex-1 py-4 ${isLast ? '' : 'border-b border-[color:var(--border-subtle)]'}`}>
              <button
                type="button"
                onClick={() => { if (status === 'done') setCurrent(i); }}
                disabled={status !== 'done'}
                className={`block text-left text-[13px] font-semibold ${
                  status === 'locked' ? 'text-[color:var(--text-muted)]' : 'text-[color:var(--text-primary)]'
                } ${status === 'done' ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
              >
                {step.title}
              </button>

              {status === 'active' && (
                <div className="mt-3">
                  {step.body}
                  {!step.final && (
                    <button
                      type="button"
                      onClick={() => setCurrent(i + 1)}
                      className="mt-4 inline-flex h-8 items-center rounded-md bg-brand px-4 text-[13px] font-semibold text-ink-onBrand transition-colors hover:bg-brand-hover"
                    >
                      {step.continueLabel || 'Done: next step'}
                    </button>
                  )}
                </div>
              )}

              {status === 'done' && (
                <p className="mt-0.5 text-[12px] text-[color:var(--text-muted)]">Completed</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

// Small inline loader row used while the connection status is loading.
export function LoadingRow() {
  return (
    <div className="flex items-center gap-2 text-[13px] text-[color:var(--text-muted)]">
      <Loader size={14} className="animate-spin" /> Loading…
    </div>
  );
}
